import type { Role } from '@wm/domain';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { TENANT_SUSPENDED_EVENT, TWO_STEP_REQUIRED_EVENT } from '../api/client';
import { useMe } from '../api/hooks';
import type { Me, MeSite } from '../api/types';

const CONFIGURE: Role[] = ['SITE_MANAGER', 'SITE_ADMIN', 'TENANT_ADMIN'];
const ADMIN_SITE: Role[] = ['SITE_ADMIN', 'TENANT_ADMIN'];
const OPERATE: Role[] = ['SITE_OPERATOR', 'SITE_MANAGER', 'SITE_ADMIN', 'TENANT_ADMIN'];
/** Contracts and costs: read, and manage (FS 4.3: operators and readers see no cost). */
const COST_READ: Role[] = [
  'SITE_MANAGER',
  'SITE_ADMIN',
  'FINANCE',
  'TENANT_ADMIN',
  'GROUP_ANALYST',
];
const COST_WRITE: Role[] = ['FINANCE'];

export interface Permissions {
  /** Waste catalogue, conditionnements, CAP, filières, prestataire activation on the site. */
  configureSite: boolean;
  /** Site preferences and origins. */
  administerSite: boolean;
  /** Tenant-level configuration: prestataires, authorizations, companies, sites. */
  administerTenant: boolean;
  /** Users and the audit log: everyone for a tenant administrator, their sites for a site admin. */
  administerUsers: boolean;
  /** Module 2: pickup requests, stock entries, create and ship expéditions. */
  operate: boolean;
  /** New requests, stock entries and chargements: not on a deactivated site (FR-PAR-04). */
  createOperations: boolean;
  /** Module 2: returns, refusals, revisions and exemptions (the site manager's rights). */
  manageOperations: boolean;
  /** Module 2: cancel a BSD before pickup. */
  cancelOperations: boolean;
  /** Costs of the site's expéditions and the contracts (FR-SUI-10, FR-SUI-11). */
  readCosts: boolean;
  /** Finance: contracts and cost lines. */
  manageCosts: boolean;
  /** The contracts page: costs visible on at least one site. */
  contracts: boolean;
  /** The group view: the roles that cover every site (FR-PIL-09). */
  group: boolean;
}

interface SiteState {
  me?: Me;
  loading: boolean;
  error: unknown;
  sites: MeSite[];
  site?: MeSite;
  siteId?: string;
  setSiteId: (id: string) => void;
  can: Permissions;
}

const SiteContext = createContext<SiteState | undefined>(undefined);
const STORAGE_KEY = 'wm.siteId';

function stored(): string | undefined {
  try {
    return localStorage.getItem(STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

export function SiteProvider({ children }: { children: ReactNode }) {
  const me = useMe();
  // the API asked for two-step verification (an administrator role was just given), or answered
  // that the vendor suspended the organisation: the profile says so, and the portal shows it
  const { refetch } = me;
  useEffect(() => {
    const onChanged = () => void refetch();
    window.addEventListener(TWO_STEP_REQUIRED_EVENT, onChanged);
    window.addEventListener(TENANT_SUSPENDED_EVENT, onChanged);
    return () => {
      window.removeEventListener(TWO_STEP_REQUIRED_EVENT, onChanged);
      window.removeEventListener(TENANT_SUSPENDED_EVENT, onChanged);
    };
  }, [refetch]);
  const [chosen, setChosen] = useState<string | undefined>(stored);
  const sites = useMemo(() => me.data?.sites ?? [], [me.data]);
  const site = sites.find((s) => s.id === chosen) ?? sites.find((s) => s.active) ?? sites[0];

  useEffect(() => {
    if (site && site.id !== chosen) setChosen(site.id);
  }, [site, chosen]);

  const setSiteId = useCallback((id: string) => {
    setChosen(id);
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch {
      /* storage unavailable */
    }
  }, []);

  const can = useMemo<Permissions>(() => {
    const operator = Boolean(me.data?.principal.platformOperator);
    const roles = site?.roles ?? [];
    const tenantRoles = me.data?.principal.tenantRoles ?? [];
    // FR-PAR-04: a deactivated site keeps its history, and its shipments on the way can still be
    // followed, but it takes no new request, stock entry or chargement
    const open = site?.active !== false;
    return {
      configureSite: operator || roles.some((r) => CONFIGURE.includes(r)),
      administerSite: operator || roles.some((r) => ADMIN_SITE.includes(r)),
      administerTenant: operator || tenantRoles.includes('TENANT_ADMIN'),
      administerUsers:
        operator ||
        tenantRoles.includes('TENANT_ADMIN') ||
        (me.data?.sites ?? []).some((s) => s.roles.includes('SITE_ADMIN')),
      operate: operator || roles.some((r) => OPERATE.includes(r)),
      createOperations: open && (operator || roles.some((r) => OPERATE.includes(r))),
      manageOperations: operator || roles.some((r) => CONFIGURE.includes(r)),
      cancelOperations: operator || roles.some((r) => ADMIN_SITE.includes(r)),
      readCosts: operator || roles.some((r) => COST_READ.includes(r)),
      manageCosts: operator || roles.some((r) => COST_WRITE.includes(r)),
      contracts:
        operator ||
        tenantRoles.some((r) => COST_READ.includes(r)) ||
        (me.data?.sites ?? []).some((s) => s.roles.some((r) => COST_READ.includes(r))),
      group: operator || tenantRoles.length > 0,
    };
  }, [me.data, site]);

  const value = useMemo<SiteState>(
    () => ({
      me: me.data,
      loading: me.isLoading,
      error: me.error,
      sites,
      site,
      siteId: site?.id,
      setSiteId,
      can,
    }),
    [me.data, me.isLoading, me.error, sites, site, setSiteId, can],
  );
  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

export function useSiteContext(): SiteState {
  const ctx = useContext(SiteContext);
  if (!ctx) throw new Error('useSiteContext must be used inside SiteProvider');
  return ctx;
}
